"use client";

import {
  BadgeDollarSign,
  FileSignature,
  Headset,
  GraduationCap,
  HeartHandshake,
  Home,
  Landmark,
  LineChart,
  LucideIcon,
  Megaphone,
  MessagesSquare,
  PiggyBank,
  Receipt,
  ShieldCheck,
  Stethoscope,
  Target,
  TrendingUp,
  Trophy,
  Users,
  Wrench,
} from "lucide-react";

// One icon per category, drawn in ink. Categories are labels, not brands —
// the accent is reserved for the active/selected state so the rail reads as
// a list of rooms, not a row of app icons.
export interface CategoryVisual {
  icon: LucideIcon;
}

const VISUALS: Record<string, CategoryVisual> = {
  general: { icon: Headset },
  "final-expense": { icon: HeartHandshake },
  "mortgage-protection": { icon: Home },
  "sales-objections": { icon: Target },
  "leads-marketing": { icon: Megaphone },
  "carriers-products": { icon: Landmark },
  underwriting: { icon: Stethoscope },
  "tech-tools": { icon: Wrench },
  wins: { icon: Trophy },
  "new-agents": { icon: GraduationCap },
  announcements: { icon: Megaphone },
  "iul-cash-value": { icon: LineChart },
  annuities: { icon: PiggyBank },
  "contracting-imos": { icon: FileSignature },
  "compliance-licensing": { icon: ShieldCheck },
  "business-money": { icon: Receipt },
  "agency-building": { icon: Users },
};

const FALLBACK: CategoryVisual = { icon: MessagesSquare };

export function categoryVisual(slug?: string | null): CategoryVisual {
  if (slug && VISUALS[slug]) return VISUALS[slug];
  return FALLBACK;
}

export function isCustomCategory(slug?: string | null): boolean {
  return !!slug && !VISUALS[slug];
}

// Small icon mark. Custom (agent-created) rooms show their initial in a
// monospace box so they still read as a room without anyone designing them.
export function CategoryIconTile({
  slug,
  size = "md",
  active = false,
}: {
  slug?: string | null;
  size?: "sm" | "md" | "lg";
  active?: boolean;
}) {
  const v = categoryVisual(slug);
  const Icon = v.icon;
  const custom = isCustomCategory(slug);
  const dims = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8 rounded-md border bg-muted/60" : "h-5 w-5";
  const iconDims = size === "sm" ? "h-3.5 w-3.5" : size === "lg" ? "h-4 w-4" : "h-4 w-4";
  const tone = active ? "text-primary" : "text-muted-foreground";
  return (
    <span className={`inline-flex shrink-0 items-center justify-center ${dims} ${tone}`}>
      {custom ? (
        <span className={`font-mono font-semibold uppercase leading-none ${size === "lg" ? "text-sm" : "text-[11px]"}`}>
          {(slug || "c")[0]}
        </span>
      ) : (
        <Icon className={iconDims} strokeWidth={2} />
      )}
    </span>
  );
}

// Extra icons reused around the forum
export { BadgeDollarSign, TrendingUp };
